import { ICartFilterBody, ICartListResponseData } from "@/api_framework/api_modals/Cart";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useCartListAPI } from "@/app/hooks/api_hooks/Cart/useCartListAPI";
import { useNewCartAPI } from "@/app/hooks/api_hooks/Cart/useNewCartAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { format, parse } from "date-fns";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICartState, setCartLevelCouponId, setCartListFilter } from "@/app_redux/reducers/slices/auth/cart_slice";
import { ClipboardDocumentIcon, ShoppingCartIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { FiLoader, FiPlusCircle } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import CartDeleteModalComponent from "./CartDeleteModalComponent";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useEMRdownloadPDF } from "@/app/hooks/api_hooks/emr/useEMRdownloadPDF";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { ROUTES } from "@/app/routes";
import { useUserBookingsListAPI } from "@/app/hooks/api_hooks/bookings/useUserBookingsListApi";
import MappedClinicAndCityPage from "@/app/routes/error_routes/MapClinicAndCityPage";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

export interface ICartListingProps {
  cartStatus: Array<string>;
  startDate: string;
  endDate: string;
}

const tableHeader = [
  { item: "S.No" },
  { item: "Pet Name" },
  { item: "Status" },
  { item: "Cart Number" },
  { item: "EMR Number" },
  { item: "Creation Date & Time" },
  { item: "Clinic" },
  { item: "Amount" },
  { item: "Created By" },
  { item: "Action" },
];

const CartListComponent = () => {
  const { getRouteKey } = useRouter();
  const isNewCartInProgress = useLoader("isNewCartInProgress");
  const mappedClinicID = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId ?? "");
  const { cartList } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const { customerPets, petId, customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;
  const mappedClinicName = useAppSelector((state) => state.root?.UIStates?.mappedClinicName);
  const params = useParams();
  const { NO_APPOINTMENTS } = useImageConstants();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const [selectionDate, setSelectionDate] = useState({
    startDate: yesterday,
    endDate: today,
    key: "selectionDate",
  });
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startDay}-${startMonth}-${startYear}`;
  const formattedEndDate = `${endDay}-${endMonth}-${endYear}`;
  const [cartId, setCartId] = useState(null);
  const [open, setOpen] = useState(false);
  const isCartListLoading = useLoader("isCustomerCartListTableInProgress");
  const { execute: fetchCartList } = useCartListAPI();
  const { execute: createNewCart } = useNewCartAPI();
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.SLOTS.id);
  const { customerBookings } = routeState.state as ISlotsState;
  const { execute: generateEmrPDf } = useEMRdownloadPDF();
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();
  const fetchCustomerBookingsAPI = useUserBookingsListAPI();
  const [isVisibleMappedClinic, setIsVisibleMappedClinic] = useState<boolean>(false);
  const [carteditDetails, setCarteditDetails] = useState(null);
  // RBAC: CART_CREATE_WITHOUT_CLINIC_MAP — legacy role: central_ops
  const isCentralOpps = useFeatureAccess("CART_CREATE_WITHOUT_CLINIC_MAP");
  const todayDay = new Date().toISOString().split("T")[0];
  const isBookingPresent = customerBookings?.data?.filter(
    (b) => b.booking_start_date === todayDay && !["Cancelled", "Failed"].includes(b.booking_status),
  );
  const isMappedClinicPresent = isBookingPresent?.some((booking) => booking?.clinic_id === mappedClinicID);

  useEffect(() => {
    const filterBody: ICartFilterBody = {
      cart_status: ["Unbilled"],
      start_date: null,
      end_date: null,
    };
    fetchCartList(filterBody, params.user_id);
    dispatch(setCartListFilter(filterBody));
  }, []);

  useEffect(() => {
    const filterBody: ICartFilterBody = {
      cart_status: ["Unbilled"],
      start_date: null,
      end_date: null,
      search_query: searchQuery ? searchQuery : null,
    };
    fetchCartList(filterBody, params.user_id);
  }, [searchQuery]);

  const handelClick = () => {
    const filterBody: ICartFilterBody = {
      cart_status: ["Unbilled"],
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      search_query: searchQuery ? searchQuery : null,
    };
    dispatch(setCartListFilter(filterBody));
    fetchCartList(filterBody, params.user_id);
  };

  useEffect(() => {
    if (params.user_id) {
      fetchUserWalletDetails(params.user_id);
    }
  }, [params.user_id]);

  const handelCartCreationFunction = (clinic_id?: any, clinic_name?: any) => {
    if (carteditDetails) {
      handelEditCart(carteditDetails);
      setCarteditDetails(null);
      setIsVisibleMappedClinic(false);
      return;
    }
    createNewCart(
      customerId,
      clinic_id ? clinic_id : mappedClinicID,
      clinic_name ? clinic_name : mappedClinicName,
    ).then((cartId) => {
      setIsVisibleMappedClinic(false);
      if (cartId) {
        window.open(
          `/app/user-cart/cart-creation/${params.user_id}/${cartId}?from_cart=true&&pet_id=${petId}`,
          "_blank",
        );
      }
    });
  };

  const handelCartCreation = () => {
    if (isMappedClinicPresent || isCentralOpps) {
      handelCartCreationFunction(null, null);
    } else {
      setIsVisibleMappedClinic(true);
    }
  };

  const handelEditCart = (cart: ICartListResponseData) => {
    dispatch(setCartLevelCouponId(cart.cart_level_coupon_id));
    window.open(`/app/user-cart/cart-creation/${cart?.customer_id}/${cart.cart_id}?from_cart=true`, "_blank");
  };

  const handleRowSelect = (cart: ICartListResponseData) => {
    setCarteditDetails(cart);
    if (isMappedClinicPresent || isCentralOpps) {
      handelEditCart(cart);
    } else {
      setIsVisibleMappedClinic(true);
    }
  };

  const viewEMRPDF = (emr: string) => {
    generateEmrPDf(emr);
  };

  useEffect(() => {
    if (customerId && petId) {
      fetchCustomerBookingsAPI(customerId, petId, {
        limit: 10,
        page: 1,
        append: false,
        filter_by: "all",
      });
    }
  }, [customerId, petId]);

  return (
    <div className="bg-white my-2">
      <div className="shadow-md rounded-md">
        <div className="pl-3 font-medium text-[10px] sm:text-xs 2zl:text-sm pb-0 mb-0">Cart List</div>
        <div className="flex flex-wrap gap-2 sm:justify-between border-gray-300 py-3 -mt-1 px-3 items-center">
          <div className="flex gap-5">
            <div className="w-70">
              <OMCalendarComponent
                selectionDate={selectionDate}
                setSelectionDate={setSelectionDate}
                startDate={formattedStartDate}
                endDate={formattedEndDate}
                handelClick={() => {
                  // handelClick();
                }}
              />
            </div>
            <button
              className="bg-[#000000] py-0 text-[10px] sm:text-xs text-white font-medium rounded-md px-3 sm:px-5 2xl:px-8"
              onClick={() => handelClick()}
            >
              Search
            </button>
          </div>
          {customerPets?.length > 0 && (
            <div>
              <RBACFlag actionId="CART_CREATION_ACTION_CTA">
                <button
                  disabled={isNewCartInProgress}
                  className="bg-[#000000] py-2 sm:py-[12px] px-4 sm:px-6 2xl:px-12 text-[10px] sm:text-xs text-white font-medium rounded-md"
                  onClick={() => handelCartCreation()}
                >
                  {isNewCartInProgress ? (
                    <span className="flex justify-start items-center gap-x-3">
                      <FiLoader className="animate-spin" /> Creating...{" "}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FiPlusCircle className="w-4 h-4 text-white" />
                      New Cart
                    </span>
                  )}
                </button>
              </RBACFlag>
            </div>
          )}
        </div>
      </div>
      <div className="my-5">
        <InputWithAddOn
          onEnterKeyPress={(query: string) => setSearchQuery(query)}
          containerClassName="w-2/12"
          addon={<ShoppingCartIcon className="w-4 h-4" />}
          placeholder="Search by Cart Number"
        />
      </div>
      {isCartListLoading ? (
        <TableSkeletonComponent columnLength={6} />
      ) : cartList?.length > 0 ? (
        <div className="bg-white mt-4 h-[600px] overflow-scroll">
          <table className="rounded-md divide-y divide-gray-200 min-w-full">
            <thead className="top-0 sticky bg-[#F1F1F1] shadow-sm rounded-md">
              <tr>
                {tableHeader?.map((key, index) => (
                  <th
                    key={index}
                    scope="col"
                    className={`p-2 text-left text-[10px] sm:text-xs font-medium text-[#000000] capitalize tracking-wider sm:sticky top-0 ${key.item === "Action" ? "sm:sticky right-0 border-l-2" : ""}`}
                  >
                    {key.item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-gray-200">
              {cartList?.map((cl, id) => (
                <tr key={id} className="hover:bg-[#F5F5F5] border-b cursor-pointer">
                  <td
                    className={`p-2 whitespace-nowrap text-xs font-normal`}
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    {id > 9 ? id : id + 1}
                  </td>
                  <td
                    className="p-2 font-normal whitespace-nowrap"
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    <div className={`text-[10px] sm:text-xs py-1`}>{cl?.emr_pet_name || "NA"}</div>
                  </td>
                  <td
                    className="p-2 font-normal text-center"
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    <div
                      className={`rounded-3xl text-[10px] sm:text-xs py-1 w-[73px] ${cl?.status === "Unbilled" ? "bg-[#FCB7B7]" : cl?.status === "Payment Due" ? "bg-red-200" : cl?.status === "Paid" ? "bg-[#65CE94]" : "bg-transparent"}`}
                    >
                      {cl?.status}
                    </div>
                  </td>
                  <td
                    className={`p-2 whitespace-nowrap text-[10px] sm:text-xs font-medium text-[#0068FF]`}
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    {cl?.cart_number}
                  </td>
                  <td
                    className={`p-2 whitespace-nowrap text-[10px] sm:text-xs font-normal text-gray-900`}
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    {cl?.emr_number}
                  </td>
                  <td
                    className={`p-2 whitespace-nowrap text-[10px] sm:text-xs font-normal text-gray-900`}
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    {format(parse(cl?.creation_date_time, "dd-MM-yyyy HH:mm:ss.SSS", new Date()), "PPpp")}
                  </td>
                  <td
                    className={`p-2 whitespace-nowrap text-[10px] sm:text-xs font-normal text-gray-900}`}
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    {cl?.clinic_name ? cl?.clinic_name : "NA"}
                  </td>
                  <td
                    className={`p-2 whitespace-nowrap text-[10px] sm:text-xs font-normal text-gray-900`}
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    {cl?.amount}
                  </td>
                  <td
                    className={`p-2 whitespace-nowrap text-[10px] sm:text-xs font-normal text-gray-900`}
                    onClick={() => {
                      handleRowSelect(cl);
                    }}
                  >
                    {cl?.created_by}
                  </td>
                  <td
                    className={`p-2 whitespace-nowrap text-[10px] sm:text-xs font-normal text-gray-900 sm:sticky right-0 border-l-2`}
                  >
                    <RBACFlag actionId="CART_DELETION_ACTION_CTA">
                      <div className="flex items-center p-4 gap-10">
                        {cl?.emr_number !== "NA" && (
                          <div
                            className="text-[#7B7B7B] flex flex-col items-center text-xs gap-1"
                            onClick={() => viewEMRPDF(cl?.emr_id)}
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                            <p>EMR PDF</p>
                          </div>
                        )}
                        <div
                          className="text-red-500 flex flex-col items-center text-xs gap-1"
                          onClick={() => {
                            setOpen(true);
                            setCartId(cl?.cart_id);
                          }}
                        >
                          <TrashIcon className="w-4 h-4" />
                          <p>Delete</p>
                        </div>
                      </div>
                    </RBACFlag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <CartDeleteModalComponent open={open} setOpen={setOpen} cart_id={cartId} />
        </div>
      ) : (
        <div className="flex justify-center bg-white rounded-md w-full h-full">
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Unbilled Cart Found." />
        </div>
      )}
      {isVisibleMappedClinic && (
        <MappedClinicAndCityPage
          isCrossVisible={true}
          message=""
          isCartEmrCreate={true}
          carteditDetails={carteditDetails}
          setCarteditDetails={setCarteditDetails}
          setIsVisibleMappedClinic={setIsVisibleMappedClinic}
          handelCartEmrCreate={(id, name) => handelCartCreationFunction(id, name)}
        />
      )}
    </div>
  );
};

export default CartListComponent;
